import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const AIReadinessAssessment = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const assessmentAreas = [
    {
      icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
      title: "Strategic Alignment",
      description: "Evaluate how AI initiatives align with business objectives and strategic priorities",
      metrics: ["Business case clarity", "Executive sponsorship", "ROI expectations", "Strategic fit"]
    },
    {
      icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
      title: "Technical Infrastructure",
      description: "Assess current technology stack, data infrastructure, and computing resources",
      metrics: ["Cloud readiness", "Data architecture", "Computing capacity", "Integration capabilities"]
    },
    {
      icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
      title: "Team Capabilities",
      description: "Review organizational skills, talent availability, and training requirements",
      metrics: ["AI expertise", "Data science skills", "Change readiness", "Training needs"]
    },
    {
      icon: "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4",
      title: "Data Maturity",
      description: "Analyze data quality, governance practices, and availability for AI applications",
      metrics: ["Data quality", "Governance framework", "Data accessibility", "Privacy compliance"]
    },
    {
      icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
      title: "Governance & Ethics",
      description: "Evaluate AI governance frameworks, ethical guidelines, and risk management",
      metrics: ["Ethics policies", "Risk framework", "Regulatory compliance", "Bias mitigation"]
    },
    {
      icon: "M13 10V3L4 14h7v7l9-11h-7z",
      title: "Innovation Culture",
      description: "Measure organizational readiness for AI adoption and change management",
      metrics: ["Innovation mindset", "Failure tolerance", "Collaboration culture", "Agile practices"]
    }
  ];

  const maturityLevels = [
    {
      level: "Level 1",
      title: "Initial/Ad-Hoc",
      description: "Minimal AI awareness, no formal strategy",
      characteristics: ["Experimental projects", "No dedicated resources", "Limited data infrastructure"]
    },
    {
      level: "Level 2",
      title: "Developing",
      description: "Emerging AI initiatives, basic infrastructure",
      characteristics: ["Pilot projects underway", "Initial data platforms", "Growing AI awareness"]
    },
    {
      level: "Level 3",
      title: "Defined",
      description: "Formal AI strategy, established practices",
      characteristics: ["Clear AI roadmap", "Dedicated AI team", "Standardized processes"]
    },
    {
      level: "Level 4",
      title: "Managed",
      description: "Scaled AI operations, measured outcomes",
      characteristics: ["Multiple AI deployments", "Performance metrics", "Cross-functional integration"]
    },
    {
      level: "Level 5",
      title: "Optimized",
      description: "AI-driven organization, continuous innovation",
      characteristics: ["AI embedded in operations", "Continuous improvement", "Industry leadership"]
    }
  ];

  const assessmentProcess = [
    {
      step: "01",
      title: "Discovery Workshop",
      duration: "1-2 days",
      description: "Collaborative sessions with key stakeholders to understand current state, objectives, and constraints",
      activities: ["Stakeholder interviews", "Documentation review", "Current state analysis", "Goal setting"]
    },
    {
      step: "02",
      title: "Comprehensive Evaluation",
      duration: "2-3 weeks",
      description: "In-depth assessment across all dimensions using frameworks, surveys, and technical audits",
      activities: ["Capability assessment", "Infrastructure audit", "Data quality review", "Skills gap analysis"]
    },
    {
      step: "03",
      title: "Findings & Recommendations",
      duration: "1 week",
      description: "Detailed report with maturity scores, gap analysis, and prioritized recommendations",
      activities: ["Maturity scoring", "Gap identification", "Risk assessment", "Roadmap development"]
    },
    {
      step: "04",
      title: "Strategic Roadmap",
      duration: "1-2 weeks",
      description: "Actionable implementation plan with milestones, resources, and success metrics",
      activities: ["Initiative prioritization", "Resource planning", "Timeline creation", "Success metrics"]
    }
  ];

  const benefits = [
    {
      title: "Clear Strategic Direction",
      description: "Understand where you are and where you need to go with AI adoption",
      metric: "85%",
      label: "Improved clarity"
    },
    {
      title: "Risk Mitigation",
      description: "Identify and address potential challenges before they become problems",
      metric: "60%",
      label: "Reduced project risk"
    },
    {
      title: "Optimized Investment",
      description: "Focus resources on initiatives with highest impact and feasibility",
      metric: "40%",
      label: "Better ROI"
    },
    {
      title: "Faster Time to Value",
      description: "Accelerate AI adoption with clear roadmap and prioritized actions",
      metric: "3-6x",
      label: "Faster deployment"
    }
  ];

  return (
    <>
      <SEO 
        title="AI Readiness Assessment - Evaluate Your AI Maturity"
        description="Comprehensive AI readiness assessment to evaluate your organization's preparedness for AI adoption. Get expert analysis, strategic roadmap, and actionable recommendations from Abacies Logiciels."
        keywords="AI readiness assessment, AI maturity model, AI adoption strategy, enterprise AI evaluation, AI capability assessment, digital transformation readiness"
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pb-20 bg-gradient-to-br from-pearl-white via-white to-blush-light/20 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #E63946 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="page-container relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blush-light/40 rounded-full mb-6 animate-fade-in">
              <span className="w-2 h-2 bg-primary-red rounded-full mr-2 animate-pulse"></span>
              <span className="text-sm font-semibold text-primary-red">Strategic Assessment</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-slide-up">
              AI Readiness <span className="text-gradient">Assessment</span>
            </h1>
            
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Evaluate your organization's AI maturity and chart a clear path to successful AI adoption with our comprehensive readiness assessment framework.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="btn-primary">
                Request Assessment
              </Link>
              <button 
                onClick={() => document.getElementById('assessment-framework').scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary"
              >
                Learn More
              </button>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              {[
                { value: "6", label: "Assessment Areas" },
                { value: "5", label: "Maturity Levels" },
                { value: "4-6", label: "Week Duration" },
                { value: "100+", label: "Metrics Evaluated" }
              ].map((stat, index) => (
                <div key={index} className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-platinum">
                  <div className="text-3xl font-bold text-primary-red mb-1">{stat.value}</div>
                  <div className="text-sm text-titanium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What is AI Readiness Assessment */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                  What is AI Readiness Assessment?
                </h2>
                <p className="text-titanium mb-4">
                  An AI Readiness Assessment is a comprehensive evaluation of your organization's preparedness to successfully adopt and scale artificial intelligence technologies. It examines technical infrastructure, organizational capabilities, data maturity, governance frameworks, and cultural readiness.
                </p>
                <p className="text-titanium mb-6">
                  Our assessment framework provides a clear understanding of your current AI maturity level, identifies gaps and opportunities, and delivers an actionable roadmap for achieving your AI objectives while managing risks and maximizing return on investment.
                </p>
                
                <div className="space-y-4">
                  {[
                    { title: "Strategic Insight", desc: "Understand your AI maturity across all dimensions" },
                    { title: "Risk Identification", desc: "Uncover potential challenges before they impact success" },
                    { title: "Clear Roadmap", desc: "Get actionable recommendations prioritized by impact" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blush-light/40 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <svg className="w-4 h-4 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-charcoal">{item.title}</h3>
                        <p className="text-sm text-titanium">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-alabaster to-pearl-white rounded-2xl p-8 border border-platinum">
                  <h3 className="text-xl font-bold mb-6 text-charcoal">Assessment Outcomes</h3>
                  <div className="space-y-4">
                    {[
                      { outcome: "Current State Analysis", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
                      { outcome: "Maturity Level Scoring", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
                      { outcome: "Gap Analysis Report", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
                      { outcome: "Strategic Roadmap", icon: "M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" },
                      { outcome: "Implementation Plan", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" }
                    ].map((item, index) => (
                      <div key={index} className="flex items-center space-x-3 bg-white p-4 rounded-lg border border-platinum">
                        <svg className="w-6 h-6 text-primary-red flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                        </svg>
                        <span className="text-charcoal font-medium">{item.outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Assessment Framework */}
      <section id="assessment-framework" className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                Comprehensive Assessment Framework
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                Our assessment evaluates six critical dimensions of AI readiness to provide a complete picture of your organization's capabilities
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {assessmentAreas.map((area, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 border border-platinum hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-blush-light/40 to-coral-mist/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-7 h-7 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={area.icon} />
                    </svg>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-2 text-charcoal">{area.title}</h3>
                  <p className="text-titanium text-sm mb-4">{area.description}</p>
                  
                  <div className="border-t border-platinum pt-4">
                    <p className="text-xs font-semibold text-graphite mb-2">Key Metrics:</p>
                    <div className="flex flex-wrap gap-2">
                      {area.metrics.map((metric, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-blush-light/20 text-primary-red rounded-full">
                          {metric}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Maturity Model */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                AI Maturity Model
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                We assess your organization against a five-level maturity model to determine your current state and chart your path forward
              </p>
            </div>

            <div className="relative">
              {/* Progress Line */}
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blush-light via-coral-mist to-primary-red transform -translate-y-1/2 opacity-20"></div>
              
              <div className="grid md:grid-cols-5 gap-6">
                {maturityLevels.map((level, index) => (
                  <div key={index} className="relative">
                    <div className="bg-white rounded-xl p-6 border-2 border-platinum hover:border-primary-red transition-all duration-300 h-full">
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blush-light/40 to-coral-mist/20 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-primary-red">
                          <span className="text-2xl font-bold text-primary-red">{index + 1}</span>
                        </div>
                        <h3 className="font-bold text-charcoal mb-1">{level.level}</h3>
                        <p className="text-sm font-semibold text-primary-red">{level.title}</p>
                      </div>
                      
                      <p className="text-sm text-titanium mb-4 text-center">{level.description}</p>
                      
                      <div className="space-y-2">
                        {level.characteristics.map((char, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <svg className="w-4 h-4 text-primary-red flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-xs text-graphite">{char}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Assessment Process */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-alabaster to-pearl-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                Our Assessment Process
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                A structured, collaborative approach that delivers actionable insights in 4-6 weeks
              </p>
            </div>

            <div className="space-y-8">
              {assessmentProcess.map((phase, index) => (
                <div key={index} className="bg-white rounded-xl p-8 border border-platinum hover:shadow-xl transition-all duration-300">
                  <div className="grid md:grid-cols-12 gap-6 items-start">
                    <div className="md:col-span-2">
                      <div className="w-20 h-20 bg-gradient-to-br from-primary-red to-deep-garnet rounded-xl flex items-center justify-center text-white font-bold text-2xl">
                        {phase.step}
                      </div>
                    </div>
                    
                    <div className="md:col-span-10">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                        <h3 className="text-2xl font-bold text-charcoal mb-2 md:mb-0">{phase.title}</h3>
                        <span className="inline-flex items-center px-4 py-1 bg-blush-light/40 rounded-full text-sm font-semibold text-primary-red">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {phase.duration}
                        </span>
                      </div>
                      
                      <p className="text-titanium mb-6">{phase.description}</p>
                      
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {phase.activities.map((activity, idx) => (
                          <div key={idx} className="flex items-center space-x-2 bg-alabaster px-3 py-2 rounded-lg">
                            <svg className="w-4 h-4 text-primary-red flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm text-graphite">{activity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits & Impact */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                Benefits & Expected Impact
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                Organizations that complete our AI readiness assessment see measurable improvements across key success factors
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-gradient-to-br from-alabaster to-pearl-white rounded-xl p-6 border border-platinum hover:shadow-xl transition-all duration-300">
                  <div className="text-center mb-4">
                    <div className="text-5xl font-bold text-primary-red mb-2">{benefit.metric}</div>
                    <div className="text-sm font-semibold text-titanium">{benefit.label}</div>
                  </div>
                  <h3 className="text-lg font-bold text-charcoal mb-2">{benefit.title}</h3>
                  <p className="text-sm text-titanium">{benefit.description}</p>
                </div>
              ))}
            </div>

            {/* Additional Benefits */}
            <div className="mt-12 bg-gradient-to-r from-primary-red to-deep-garnet rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6 text-center">Additional Value Delivered</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { title: "Executive Alignment", desc: "Build consensus around AI strategy and priorities" },
                  { title: "Vendor Selection", desc: "Make informed decisions on technology partners" },
                  { title: "Budget Justification", desc: "Support investment cases with data-driven insights" },
                  { title: "Talent Planning", desc: "Identify skill gaps and training requirements" },
                  { title: "Quick Wins", desc: "Identify high-impact, low-complexity opportunities" },
                  { title: "Risk Awareness", desc: "Understand and prepare for potential challenges" }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">{item.title}</h4>
                      <p className="text-sm opacity-90">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Abacies */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                Why Choose Abacies for Your Assessment
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                Leverage our deep expertise and proven methodology to accelerate your AI journey
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {[
                {
                  icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
                  title: "Industry Expertise",
                  description: "12+ years helping enterprises across healthcare, finance, manufacturing, and more adopt AI successfully"
                },
                {
                  icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
                  title: "Proven Framework",
                  description: "Battle-tested assessment methodology refined through 100+ successful evaluations"
                },
                {
                  icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
                  title: "Expert Team",
                  description: "Dedicated AI consultants, data scientists, and strategists guide your assessment"
                },
                {
                  icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
                  title: "Rapid Delivery",
                  description: "Comprehensive assessment completed in 4-6 weeks with minimal disruption"
                },
                {
                  icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
                  title: "Actionable Insights",
                  description: "Practical recommendations with clear priorities, not just theoretical analysis"
                },
                {
                  icon: "M13 10V3L4 14h7v7l9-11h-7z",
                  title: "Implementation Support",
                  description: "Optional follow-on services to help execute the roadmap and achieve results"
                }
              ].map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-6 border border-platinum hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 bg-blush-light/40 rounded-xl flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={feature.icon} />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-charcoal mb-2">{feature.title}</h3>
                  <p className="text-titanium text-sm">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Success Metrics */}
            <div className="bg-white rounded-2xl p-8 border border-platinum">
              <h3 className="text-2xl font-bold text-center mb-8 text-charcoal">Our Track Record</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: "100+", label: "Assessments Completed" },
                  { value: "20", label: "Industries Served" },
                  { value: "95%", label: "Client Satisfaction" },
                  { value: "85%", label: "Implementation Success" }
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-4xl font-bold text-primary-red mb-2">{stat.value}</div>
                    <div className="text-sm text-titanium">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-primary-red to-deep-garnet text-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Evaluate Your AI Readiness?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Start with a complimentary consultation to discuss your AI objectives and learn how our assessment can help you achieve them faster and with less risk.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
                Schedule Free Consultation
              </Link>
              <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
                Explore AI Solutions
              </Link>
            </div>
            <p className="mt-6 text-sm opacity-75">
              No obligation • Typical assessment: 4-6 weeks • Flexible engagement models
            </p>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-charcoal text-center">Related AI Services</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/services/strategic-consultation" className="card hover:shadow-xl transition-all duration-300 group">
                <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red">Strategic Consultation</h3>
                <p className="text-titanium mb-4">Expert guidance on AI strategy, roadmap development, and transformation planning</p>
                <span className="text-primary-red font-semibold">Learn More →</span>
              </Link>
              <Link to="/services/ai-machine-learning" className="card hover:shadow-xl transition-all duration-300 group">
                <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red">AI & Machine Learning</h3>
                <p className="text-titanium mb-4">Custom AI solutions, model development, and intelligent automation services</p>
                <span className="text-primary-red font-semibold">Learn More →</span>
              </Link>
              <Link to="/services/digital-transformation" className="card hover:shadow-xl transition-all duration-300 group">
                <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red">Digital Transformation</h3>
                <p className="text-titanium mb-4">End-to-end transformation services to modernize your enterprise</p>
                <span className="text-primary-red font-semibold">Learn More →</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AIReadinessAssessment;